import {
  BatchWriteItemCommand,
  DynamoDBClient,
  type WriteRequest,
} from "@aws-sdk/client-dynamodb";
import { fetchAuthSession } from "aws-amplify/auth";
import type { CloudConfig } from "./cloudConfig";
import { registerAwsCredentialClientReset } from "./awsCredentialClientRegistry";

const K_FLUSH_INTERVAL_MS = 10_000;
const K_FLUSH_SIZE_THRESHOLD = 25;
const K_MAX_BATCH_SIZE = 25;
const K_MAX_RETRIES = 5;

/**
 * Small Dynamo batch queue shared by observational event/transcript writers.
 * It owns transport/retry only; each service remains responsible for its item
 * keys and attributes.
 */
export class AwsBatchWriteQueue {
  private readonly m_pending: WriteRequest[] = [];
  private m_client: DynamoDBClient;
  private readonly m_timer: number;
  private readonly m_unregisterCredentialReset: () => void;
  private m_flushPromise: Promise<void> | null = null;
  private m_sessionGeneration = 0;

  constructor(
    private readonly m_tableName: string,
    private readonly m_config: CloudConfig,
  ) {
    this.m_client = this.createClient();
    this.m_unregisterCredentialReset = registerAwsCredentialClientReset(
      () => this.resetAuthenticatedSession(),
    );
    this.m_timer = window.setInterval(() => {
      void this.flushPending();
    }, K_FLUSH_INTERVAL_MS);
  }

  private createClient(): DynamoDBClient {
    return new DynamoDBClient({
      region: this.m_config.region,
      credentials: async () => {
        const session = await fetchAuthSession();
        const credentials = session.credentials;
        if (!credentials?.accessKeyId || !credentials.secretAccessKey) {
          throw new Error("[AwsBatchWriteQueue] No AWS credentials available.");
        }
        return {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
          expiration: credentials.expiration,
        };
      },
    });
  }

  private resetAuthenticatedSession(): void {
    this.m_sessionGeneration += 1;
    this.m_pending.length = 0;
    this.m_flushPromise = null;
    this.m_client.destroy();
    this.m_client = this.createClient();
  }

  enqueue(request: WriteRequest): void {
    this.m_pending.push(request);
    if (this.m_pending.length >= K_FLUSH_SIZE_THRESHOLD) {
      void this.flushPending();
    }
  }

  flushPending(): Promise<void> {
    if (this.m_flushPromise) return this.m_flushPromise;
    if (this.m_pending.length === 0) return Promise.resolve();

    const generation = this.m_sessionGeneration;
    const client = this.m_client;
    const batch = this.m_pending.splice(0, this.m_pending.length);
    const flushPromise = this.persist(client, batch)
      .catch((error: unknown) => {
        // A logout deliberately drops the outgoing user's observational rows.
        // Never retry them with the next user's credentials.
        if (generation !== this.m_sessionGeneration) return;
        // Preserve order and retry on the next interval instead of dropping
        // records because of a transient credential/network failure.
        this.m_pending.unshift(...batch);
        console.warn("[AwsBatchWriteQueue] batch write failed; queued for retry:", error);
      })
      .finally(() => {
        if (this.m_flushPromise !== flushPromise) return;
        this.m_flushPromise = null;
        if (this.m_pending.length >= K_FLUSH_SIZE_THRESHOLD) {
          void this.flushPending();
        }
      });
    this.m_flushPromise = flushPromise;
    return flushPromise;
  }

  dispose(): Promise<void> {
    window.clearInterval(this.m_timer);
    this.m_unregisterCredentialReset();
    return this.flushPending().finally(() => this.m_client.destroy());
  }

  private async persist(
    client: DynamoDBClient,
    requests: ReadonlyArray<WriteRequest>,
  ): Promise<void> {
    for (let offset = 0; offset < requests.length; offset += K_MAX_BATCH_SIZE) {
      let pending = requests.slice(offset, offset + K_MAX_BATCH_SIZE);
      for (let attempt = 0; attempt < K_MAX_RETRIES && pending.length > 0; attempt += 1) {
        const response = await client.send(
          new BatchWriteItemCommand({
            RequestItems: { [this.m_tableName]: pending },
          }),
        );
        pending = response.UnprocessedItems?.[this.m_tableName] ?? [];
      }
      if (pending.length > 0) {
        throw new Error(`${pending.length} DynamoDB items remained unprocessed.`);
      }
    }
  }
}
